<script lang="ts" setup>
import { computed, onMounted, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import {
  type AccountDetails as AccountDetailsType,
  type PaginatedResponse,
  type AdminDomainListItem as AdminDomainListItemType,
  type AdminHostingSiteRow as AdminHostingSiteRowType,
  type AdminHandleListItem as AdminHandleListItemType,
  type AccountSplit as AccountSplitType,
  type CreateAccountSplitRequest as CreateAccountSplitRequestType,
  type AddAccountSplitItemRequest as AddAccountSplitItemRequestType,
  type AccountSplitItemAssetType,
  type DryRunResult as DryRunResultType,
  type MailAccount as MailAccountType
} from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import AccountSplitDryRunDialog from './AccountSplitDryRunDialog.vue'

defineOptions({ name: 'AccountSplitBuilder' })

const { t } = useI18n()
const router = useRouter()
const notifyStore = useNotifyStore()

// ---------- Source / Target account selection state ----------

type TargetMode = 'existing_account' | 'new_account'

type AssetSelection = {
  asset_type: AccountSplitItemAssetType
  asset_id: number
}

type SubscriptionRow = {
  id: number
  product?: { name?: string; slug?: string | null }
  status?: string
  billing_freq?: string
  domain?: { name?: string }
}

const data = reactive({
  // Loading + saving
  loadingAccounts: false,
  loadingAssets: false,
  saving: false,
  runningDryRun: false,

  // Account list (cached, client filtered)
  allAccounts: [] as AccountDetailsType[],

  // Source selection
  sourceAccount: null as AccountDetailsType | null,
  sourceSearch: '',
  sourceDropdownOpen: false,

  // Target selection
  targetMode: 'existing_account' as TargetMode,
  targetAccount: null as AccountDetailsType | null,
  targetSearch: '',
  targetDropdownOpen: false,

  // New-account form
  newAccount: {
    type: 'person' as 'person' | 'organization',
    email: '',
    name: '',
    surname: '',
    org: '',
    line1: '',
    line2: '',
    postcode: '',
    city: '',
    country_iso: 'DE',
    phone: '',
    lang: 'de' as 'de' | 'en'
  },

  // Notes
  notes: '',

  // Persisted split state
  splitId: null as number | null,

  // Asset selection
  selectedAssets: [] as AssetSelection[],
  subscriptions: [] as SubscriptionRow[],
  domains: [] as AdminDomainListItemType[],
  sites: [] as AdminHostingSiteRowType[],
  handles: [] as AdminHandleListItemType[],
  mailboxes: [] as MailAccountType[],

  // Dry run state
  showDryRunDialog: false,
  dryRunResult: null as DryRunResultType | null
})

// ---------- Helpers ----------

const accountLabel = (acc: AccountDetailsType | null): string => {
  if (!acc) return ''
  const name = [acc.name, acc.surname].filter(Boolean).join(' ')
  const base = acc.account_type === 'organization' && acc.org ? acc.org : name || acc.email
  return acc.id ? `${base} (#${acc.id})` : base
}

const filterAccounts = (query: string, excludeId?: number): AccountDetailsType[] => {
  const q = query.trim().toLowerCase()
  let list = data.allAccounts
  if (excludeId !== undefined) {
    list = list.filter((a) => a.id !== excludeId)
  }
  if (!q) return list.slice(0, 50)
  return list
    .filter((a) => {
      const email = (a.email || '').toLowerCase()
      const name = `${a.name || ''} ${a.surname || ''}`.toLowerCase()
      const org = (a.org || '').toLowerCase()
      return email.includes(q) || name.includes(q) || org.includes(q)
    })
    .slice(0, 50)
}

const sourceCandidates = (): AccountDetailsType[] => filterAccounts(data.sourceSearch)
const targetCandidates = (): AccountDetailsType[] =>
  filterAccounts(data.targetSearch, data.sourceAccount?.id)

// ---------- Load accounts ----------

const loadAllAccounts = async () => {
  data.loadingAccounts = true
  try {
    const res = await useAxios<null, PaginatedResponse<AccountDetailsType>>(
      'GET',
      '/account/all?limit=200',
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data?.data) {
      data.allAccounts = res.data.data as AccountDetailsType[]
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:loadAccounts' })
  } finally {
    data.loadingAccounts = false
  }
}

// ---------- Asset loaders ----------

const loadSubscriptions = async (accId: number) => {
  try {
    const res = await useAxios<null, SubscriptionRow[]>('GET', `/account/${accId}/subs`, true)
    if (res && res.status === STATUS_CODES.OK && Array.isArray(res.data)) {
      data.subscriptions = res.data
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:loadSubs' })
  }
}

const loadDomains = async (accId: number) => {
  try {
    const res = await useAxios<null, PaginatedResponse<AdminDomainListItemType>>(
      'GET',
      `/admin/domains?account_id=${accId}&limit=200`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data?.data) {
      data.domains = res.data.data as AdminDomainListItemType[]
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:loadDomains' })
  }
}

const loadSites = async (accId: number) => {
  try {
    const params = new URLSearchParams({ account_id: String(accId), limit: '200' })
    const res = await useAxios<null, { data: AdminHostingSiteRowType[]; total: number }>(
      'GET',
      `/admin/hosting/sites?${params.toString()}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data?.data) {
      data.sites = res.data.data as AdminHostingSiteRowType[]
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:loadSites' })
  }
}

const loadHandles = async (accId: number) => {
  try {
    const res = await useAxios<null, PaginatedResponse<AdminHandleListItemType>>(
      'GET',
      `/admin/handles?account_id=${accId}&limit=200`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data?.data) {
      data.handles = res.data.data as AdminHandleListItemType[]
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:loadHandles' })
  }
}

const loadMailboxes = async (accId: number) => {
  try {
    const res = await useAxios<null, MailAccountType[]>(
      'GET',
      `/mail?account_id=${accId}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && Array.isArray(res.data)) {
      data.mailboxes = res.data
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:loadMailboxes' })
  }
}

const loadSourceAssets = async () => {
  if (!data.sourceAccount?.id) return
  data.loadingAssets = true
  data.subscriptions = []
  data.domains = []
  data.sites = []
  data.handles = []
  data.mailboxes = []
  try {
    await Promise.all([
      loadSubscriptions(data.sourceAccount.id),
      loadDomains(data.sourceAccount.id),
      loadSites(data.sourceAccount.id),
      loadHandles(data.sourceAccount.id),
      loadMailboxes(data.sourceAccount.id)
    ])
  } finally {
    data.loadingAssets = false
  }
}

// ---------- Selection helpers ----------

const isSelected = (asset_type: AccountSplitItemAssetType, asset_id: number): boolean => {
  return data.selectedAssets.some(
    (a) => a.asset_type === asset_type && a.asset_id === asset_id
  )
}

const toggleAsset = (asset_type: AccountSplitItemAssetType, asset_id: number) => {
  if (isSelected(asset_type, asset_id)) {
    data.selectedAssets = data.selectedAssets.filter(
      (a) => !(a.asset_type === asset_type && a.asset_id === asset_id)
    )
  } else {
    data.selectedAssets = [...data.selectedAssets, { asset_type, asset_id }]
  }
}

// ---------- Source selection handling ----------

const pickSource = (acc: AccountDetailsType) => {
  data.sourceAccount = acc
  data.sourceSearch = ''
  data.sourceDropdownOpen = false
  // Reset selections when source changes
  data.selectedAssets = []
  data.splitId = null
  data.dryRunResult = null
  loadSourceAssets()
}

const clearSource = () => {
  data.sourceAccount = null
  data.selectedAssets = []
  data.subscriptions = []
  data.domains = []
  data.sites = []
  data.handles = []
  data.splitId = null
  data.dryRunResult = null
}

// ---------- Target selection ----------

const pickTarget = (acc: AccountDetailsType) => {
  data.targetAccount = acc
  data.targetSearch = ''
  data.targetDropdownOpen = false
}

const clearTarget = () => {
  data.targetAccount = null
}

const onSourceFocus = () => {
  data.sourceDropdownOpen = true
}
const onSourceBlur = () => {
  setTimeout(() => {
    data.sourceDropdownOpen = false
  }, 150)
}
const onTargetFocus = () => {
  data.targetDropdownOpen = true
}
const onTargetBlur = () => {
  setTimeout(() => {
    data.targetDropdownOpen = false
  }, 150)
}

// ---------- Validation ----------

const canSave = computed(() => {
  if (!data.sourceAccount?.id) return false
  if (data.targetMode === 'existing_account') {
    return !!data.targetAccount?.id
  }
  // New account: require minimum fields
  const n = data.newAccount
  return !!(
    n.email &&
    n.line1 &&
    n.city &&
    n.postcode &&
    n.country_iso &&
    n.phone
  )
})

// ---------- Persistence helpers ----------

const buildCreatePayload = (): CreateAccountSplitRequestType | null => {
  if (!data.sourceAccount?.id) return null
  const payload: CreateAccountSplitRequestType = {
    source_account_id: data.sourceAccount.id,
    target_mode: data.targetMode
  }
  if (data.notes.trim()) payload.notes = data.notes.trim()
  if (data.targetMode === 'existing_account' && data.targetAccount?.id) {
    payload.target_account_id = data.targetAccount.id
  } else if (data.targetMode === 'new_account') {
    payload.new_account_payload = { ...data.newAccount }
  }
  return payload
}

const persistDraft = async (): Promise<number | null> => {
  // If already saved, return existing id
  if (data.splitId) return data.splitId

  const payload = buildCreatePayload()
  if (!payload) return null

  try {
    const res = await useAxios<CreateAccountSplitRequestType, AccountSplitType>(
      'POST',
      '/admin/account-splits',
      true,
      payload
    )
    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.CREATED)) {
      const created = res.data
      if (created && typeof created.id === 'number') {
        data.splitId = created.id
        return created.id
      }
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:create' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  }
  return null
}

const syncItemsToServer = async (splitId: number): Promise<boolean> => {
  // Push each selected asset that isn't yet on the server.
  // For simplicity we add all selected; backend should be idempotent or we
  // accept duplicates (in practice the backend rejects duplicates and we just
  // catch the error). To keep this clean, we re-fetch first then diff.
  try {
    const res = await useAxios<null, AccountSplitType>(
      'GET',
      `/admin/account-splits/${splitId}`,
      true
    )
    if (!res || res.status !== STATUS_CODES.OK) return false
    const existing = res.data
    const existingKeys = new Set(
      (existing.items || []).map((i) => `${i.asset_type}:${i.asset_id}`)
    )
    // Add missing
    const toAdd = data.selectedAssets.filter(
      (a) => !existingKeys.has(`${a.asset_type}:${a.asset_id}`)
    )
    for (const item of toAdd) {
      try {
        await useAxios<AddAccountSplitItemRequestType, unknown>(
          'POST',
          `/admin/account-splits/${splitId}/items`,
          true,
          { asset_type: item.asset_type, asset_id: item.asset_id }
        )
      } catch (error) {
        handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:addItem' })
      }
    }
    // Remove that aren't selected
    const selectedKeys = new Set(
      data.selectedAssets.map((a) => `${a.asset_type}:${a.asset_id}`)
    )
    const toRemove = (existing.items || []).filter(
      (i) => !selectedKeys.has(`${i.asset_type}:${i.asset_id}`)
    )
    for (const item of toRemove) {
      try {
        await useAxios<null, unknown>(
          'DELETE',
          `/admin/account-splits/${splitId}/items/${item.id}`,
          true
        )
      } catch (error) {
        handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:removeItem' })
      }
    }
    return true
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:syncItems' })
    return false
  }
}

// ---------- Actions ----------

const saveDraft = async () => {
  if (!canSave.value || data.saving) return
  data.saving = true
  try {
    const splitId = await persistDraft()
    if (!splitId) return
    await syncItemsToServer(splitId)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('common.success'),
      message: t('dashboard.admin.accountSplit.notify.draftSaved')
    })
  } finally {
    data.saving = false
  }
}

const runDryRun = async () => {
  if (!canSave.value || data.runningDryRun) return
  data.runningDryRun = true
  try {
    const splitId = await persistDraft()
    if (!splitId) return
    const synced = await syncItemsToServer(splitId)
    if (!synced) return

    const res = await useAxios<null, { dry_run_result?: DryRunResultType } | DryRunResultType>(
      'POST',
      `/admin/account-splits/${splitId}/dry-run`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data) {
      // Backend may return either the DryRunResult directly or the full split with `dry_run_result`.
      const body = res.data as Record<string, unknown>
      let result: DryRunResultType | null = null
      if (body && typeof body === 'object' && 'dry_run_result' in body) {
        const inner = (body as { dry_run_result?: DryRunResultType }).dry_run_result
        if (inner) result = inner
      }
      if (!result && body && typeof body === 'object' && 'items' in body && 'source' in body) {
        result = body as unknown as DryRunResultType
      }
      data.dryRunResult = result
      data.showDryRunDialog = true
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('common.success'),
        message: t('dashboard.admin.accountSplit.notify.dryRunSuccess')
      })
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitBuilder:dryRun' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  } finally {
    data.runningDryRun = false
  }
}

const handleDialogClose = () => {
  data.showDryRunDialog = false
}

const handleExecuted = () => {
  // Dialog handles routing on execute; nothing extra to do here.
}

watch(
  () => data.targetMode,
  () => {
    // Clear stale state when switching modes
    if (data.targetMode === 'existing_account') {
      // keep targetAccount
    } else {
      data.targetAccount = null
    }
  }
)

// Helper for v-for over assets (plain functions per styleguide for large SFCs)
const subscriptionRows = () => data.subscriptions
const domainRows = () => data.domains
const siteRows = () => data.sites
const handleRows = () => data.handles
const mailboxRows = () => data.mailboxes

onMounted(() => {
  loadAllAccounts()
})

const goBack = () => router.push({ name: 'dashboardAdminAccountSplit' })
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.accountSplit.title')"
    :subtitle="$t('dashboard.admin.accountSplit.builder.assetPicker')"
    icon="mdi-call-split"
    icon-color="purple"
    show-back
    @back="goBack"
  >
    <!-- 1. Source account -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.admin.accountSplit.builder.sourceLabel') }}</h3>
      </div>

      <div v-if="data.sourceAccount" class="selected-chip">
        <v-icon icon="mdi-account-circle" size="18" />
        <span class="chip-label">{{ accountLabel(data.sourceAccount) }}</span>
        <span class="chip-email">{{ data.sourceAccount.email }}</span>
        <button class="chip-clear" :title="$t('dashboard.admin.accountSplit.builder.clearSelection')" @click="clearSource">
          <v-icon icon="mdi-close" size="14" />
        </button>
      </div>

      <div v-else class="search-block">
        <div class="dashboard-search-wrapper">
          <v-icon icon="mdi-magnify" size="18" class="search-icon" />
          <input
            v-model="data.sourceSearch"
            type="text"
            class="dashboard-search-input"
            :placeholder="t('dashboard.admin.accountSplit.builder.searchPlaceholder')"
            @focus="onSourceFocus"
            @blur="onSourceBlur"
          />
        </div>
        <div v-if="data.sourceDropdownOpen" class="dropdown">
          <div v-if="data.loadingAccounts" class="state-box">
            <v-progress-circular indeterminate size="24" width="2" color="#00B0FF" />
          </div>
          <div v-else-if="sourceCandidates().length === 0" class="state-box">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noResults') }}</p>
          </div>
          <ul v-else class="dropdown-list">
            <li v-for="acc in sourceCandidates()" :key="acc.id" class="dropdown-item" @mousedown.prevent="pickSource(acc)">
              <span class="dropdown-name">{{ accountLabel(acc) }}</span>
              <span class="dropdown-email">{{ acc.email }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="notes-block">
        <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.notesLabel') }}</label>
        <textarea
          v-model="data.notes"
          class="form-textarea"
          rows="2"
          :placeholder="t('dashboard.admin.accountSplit.builder.notesPlaceholder')"
        />
      </div>
    </div>

    <!-- 2. Target -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.admin.accountSplit.builder.targetLabel') }}</h3>
      </div>

      <div class="tab-bar">
        <button
          class="tab-item"
          :class="{ active: data.targetMode === 'existing_account' }"
          @click="data.targetMode = 'existing_account'"
        >
          {{ $t('dashboard.admin.accountSplit.targetMode.existing_account') }}
        </button>
        <button
          class="tab-item"
          :class="{ active: data.targetMode === 'new_account' }"
          @click="data.targetMode = 'new_account'"
        >
          {{ $t('dashboard.admin.accountSplit.targetMode.new_account') }}
        </button>
      </div>

      <!-- Existing account -->
      <div v-if="data.targetMode === 'existing_account'">
        <div v-if="data.targetAccount" class="selected-chip">
          <v-icon icon="mdi-account-circle" size="18" />
          <span class="chip-label">{{ accountLabel(data.targetAccount) }}</span>
          <span class="chip-email">{{ data.targetAccount.email }}</span>
          <button class="chip-clear" :title="$t('dashboard.admin.accountSplit.builder.clearSelection')" @click="clearTarget">
            <v-icon icon="mdi-close" size="14" />
          </button>
        </div>
        <div v-else class="search-block">
          <div v-if="!data.sourceAccount" class="state-box">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.pickSourceFirst') }}</p>
          </div>
          <template v-else>
            <div class="dashboard-search-wrapper">
              <v-icon icon="mdi-magnify" size="18" class="search-icon" />
              <input
                v-model="data.targetSearch"
                type="text"
                class="dashboard-search-input"
                :placeholder="t('dashboard.admin.accountSplit.builder.searchPlaceholder')"
                @focus="onTargetFocus"
                @blur="onTargetBlur"
              />
            </div>
            <div v-if="data.targetDropdownOpen" class="dropdown">
              <div v-if="targetCandidates().length === 0" class="state-box">
                <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noResults') }}</p>
              </div>
              <ul v-else class="dropdown-list">
                <li v-for="acc in targetCandidates()" :key="acc.id" class="dropdown-item" @mousedown.prevent="pickTarget(acc)">
                  <span class="dropdown-name">{{ accountLabel(acc) }}</span>
                  <span class="dropdown-email">{{ acc.email }}</span>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>

      <!-- New account -->
      <div v-else class="new-account-form">
        <p class="form-hint">{{ $t('dashboard.admin.accountSplit.builder.newAccountIntro') }}</p>

        <div class="form-row two-col">
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.type') }}</label>
            <select v-model="data.newAccount.type" class="form-input">
              <option value="person">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.typePerson') }}</option>
              <option value="organization">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.typeOrganization') }}</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.language') }}</label>
            <select v-model="data.newAccount.lang" class="form-input">
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label required">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.email') }}</label>
            <input v-model="data.newAccount.email" type="email" class="form-input" placeholder="email@example.com" />
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.firstName') }}</label>
            <input v-model="data.newAccount.name" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.lastName') }}</label>
            <input v-model="data.newAccount.surname" type="text" class="form-input" />
          </div>
        </div>

        <div v-if="data.newAccount.type === 'organization'" class="form-row">
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.organization') }}</label>
            <input v-model="data.newAccount.org" type="text" class="form-input" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label required">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.line1') }}</label>
            <input v-model="data.newAccount.line1" type="text" class="form-input" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.line2') }}</label>
            <input v-model="data.newAccount.line2" type="text" class="form-input" />
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-group">
            <label class="form-label required">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.postcode') }}</label>
            <input v-model="data.newAccount.postcode" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label class="form-label required">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.city') }}</label>
            <input v-model="data.newAccount.city" type="text" class="form-input" />
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-group">
            <label class="form-label required">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.countryIso') }}</label>
            <input v-model="data.newAccount.country_iso" type="text" maxlength="2" class="form-input" placeholder="DE" />
          </div>
          <div class="form-group">
            <label class="form-label required">{{ $t('dashboard.admin.accountSplit.builder.newAccountForm.phone') }}</label>
            <input v-model="data.newAccount.phone" type="tel" class="form-input" placeholder="+49 30 1234567" />
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Assets -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.admin.accountSplit.builder.assetPicker') }}</h3>
      </div>

      <div v-if="!data.sourceAccount" class="state-box">
        <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.pickSourceFirst') }}</p>
      </div>
      <div v-else-if="data.loadingAssets" class="state-box">
        <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
      </div>
      <template v-else>
        <!-- Subscriptions -->
        <div class="asset-group">
          <h4 class="asset-group-title">
            <v-icon icon="mdi-cube-outline" size="18" />
            <span>{{ $t('dashboard.admin.accountSplit.builder.assetSubscriptions') }}</span>
            <span class="group-count">{{ subscriptionRows().length }}</span>
          </h4>
          <div v-if="subscriptionRows().length === 0" class="state-box small">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noAssets') }}</p>
          </div>
          <div v-else class="data-table">
            <div class="table-header sub-cols">
              <span class="col col-check"></span>
              <span class="col">ID</span>
              <span class="col">Product</span>
              <span class="col">Domain</span>
              <span class="col">Status</span>
              <span class="col">Billing</span>
            </div>
            <label
              v-for="sub in subscriptionRows()"
              :key="sub.id"
              class="table-row sub-cols"
              :class="{ selected: isSelected('subscription', sub.id) }"
            >
              <span class="col col-check">
                <input
                  type="checkbox"
                  :checked="isSelected('subscription', sub.id)"
                  @change="toggleAsset('subscription', sub.id)"
                />
              </span>
              <span class="col mono">#{{ sub.id }}</span>
              <span class="col">{{ sub.product?.name || sub.product?.slug || '—' }}</span>
              <span class="col mono">{{ sub.domain?.name || '—' }}</span>
              <span class="col">{{ sub.status || '—' }}</span>
              <span class="col">{{ sub.billing_freq || '—' }}</span>
            </label>
          </div>
        </div>

        <!-- Domains -->
        <div class="asset-group">
          <h4 class="asset-group-title">
            <v-icon icon="mdi-web" size="18" />
            <span>{{ $t('dashboard.admin.accountSplit.builder.assetDomains') }}</span>
            <span class="group-count">{{ domainRows().length }}</span>
          </h4>
          <div v-if="domainRows().length === 0" class="state-box small">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noAssets') }}</p>
          </div>
          <div v-else class="data-table">
            <div class="table-header domain-cols">
              <span class="col col-check"></span>
              <span class="col">ID</span>
              <span class="col">Name</span>
              <span class="col">Status</span>
            </div>
            <label
              v-for="d in domainRows()"
              :key="d.id"
              class="table-row domain-cols"
              :class="{ selected: isSelected('domain', d.id) }"
            >
              <span class="col col-check">
                <input
                  type="checkbox"
                  :checked="isSelected('domain', d.id)"
                  @change="toggleAsset('domain', d.id)"
                />
              </span>
              <span class="col mono">#{{ d.id }}</span>
              <span class="col">{{ d.name }}</span>
              <span class="col">{{ d.status }}</span>
            </label>
          </div>
        </div>

        <!-- Handles -->
        <div class="asset-group">
          <h4 class="asset-group-title">
            <v-icon icon="mdi-card-account-details-outline" size="18" />
            <span>{{ $t('dashboard.admin.accountSplit.builder.assetHandles') }}</span>
            <span class="group-count">{{ handleRows().length }}</span>
          </h4>
          <div v-if="handleRows().length === 0" class="state-box small">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noAssets') }}</p>
          </div>
          <div v-else class="data-table">
            <div class="table-header handle-cols">
              <span class="col col-check"></span>
              <span class="col">ID</span>
              <span class="col">External</span>
              <span class="col">Type</span>
            </div>
            <label
              v-for="h in handleRows()"
              :key="h.id"
              class="table-row handle-cols"
              :class="{ selected: isSelected('handle', h.id) }"
            >
              <span class="col col-check">
                <input
                  type="checkbox"
                  :checked="isSelected('handle', h.id)"
                  @change="toggleAsset('handle', h.id)"
                />
              </span>
              <span class="col mono">#{{ h.id }}</span>
              <span class="col">{{ h.external_name || '—' }}</span>
              <span class="col">{{ h.type }}</span>
            </label>
          </div>
        </div>

        <!-- Hosting sites -->
        <div class="asset-group">
          <h4 class="asset-group-title">
            <v-icon icon="mdi-web-box" size="18" />
            <span>{{ $t('dashboard.admin.accountSplit.builder.assetSites') }}</span>
            <span class="group-count">{{ siteRows().length }}</span>
          </h4>
          <div v-if="siteRows().length === 0" class="state-box small">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noAssets') }}</p>
          </div>
          <div v-else class="data-table">
            <div class="table-header site-cols">
              <span class="col col-check"></span>
              <span class="col">ID</span>
              <span class="col">Name</span>
              <span class="col">Status</span>
              <span class="col">Domain</span>
            </div>
            <label
              v-for="s in siteRows()"
              :key="s.id"
              class="table-row site-cols"
              :class="{ selected: isSelected('hosting_site', s.id) }"
            >
              <span class="col col-check">
                <input
                  type="checkbox"
                  :checked="isSelected('hosting_site', s.id)"
                  @change="toggleAsset('hosting_site', s.id)"
                />
              </span>
              <span class="col mono">#{{ s.id }}</span>
              <span class="col">{{ s.name }}</span>
              <span class="col">{{ s.status }}</span>
              <span class="col">{{ s.primary_domain || '—' }}</span>
            </label>
          </div>
        </div>

        <!-- Mailboxes -->
        <div class="asset-group">
          <h4 class="asset-group-title">
            <v-icon icon="mdi-email-outline" size="18" />
            <span>{{ $t('dashboard.admin.accountSplit.builder.assetMailboxes') }}</span>
            <span class="group-count">{{ mailboxRows().length }}</span>
          </h4>
          <div v-if="mailboxRows().length === 0" class="state-box small">
            <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.builder.noAssets') }}</p>
          </div>
          <div v-else class="data-table">
            <div class="table-header mailbox-cols">
              <span class="col col-check"></span>
              <span class="col">ID</span>
              <span class="col">Email</span>
              <span class="col">Type</span>
              <span class="col">Status</span>
            </div>
            <label
              v-for="m in mailboxRows()"
              :key="m.id"
              class="table-row mailbox-cols"
              :class="{ selected: isSelected('mailbox', m.id) }"
            >
              <span class="col col-check">
                <input
                  type="checkbox"
                  :checked="isSelected('mailbox', m.id)"
                  @change="toggleAsset('mailbox', m.id)"
                />
              </span>
              <span class="col mono">#{{ m.id }}</span>
              <span class="col">{{ m.email }}</span>
              <span class="col">{{ m.type }}</span>
              <span class="col">{{ m.active ? 'active' : 'inactive' }}</span>
            </label>
          </div>
        </div>
      </template>
    </div>

    <!-- Action bar -->
    <div class="action-bar">
      <button class="btn-text" :disabled="!canSave || data.saving" @click="saveDraft">
        <v-progress-circular
          v-if="data.saving"
          indeterminate
          size="14"
          width="2"
          color="#00B0FF"
        />
        <template v-else>
          <v-icon icon="mdi-content-save-outline" size="16" />
          <span>{{ $t('dashboard.admin.accountSplit.actions.save') }}</span>
        </template>
      </button>
      <button class="btn-create" :disabled="!canSave || data.runningDryRun" @click="runDryRun">
        <v-progress-circular
          v-if="data.runningDryRun"
          indeterminate
          size="14"
          width="2"
          color="white"
        />
        <template v-else>
          <v-icon icon="mdi-play-circle-outline" size="16" />
          <span>{{ $t('dashboard.admin.accountSplit.actions.dryRun') }}</span>
        </template>
      </button>
    </div>

    <!-- Dry-run dialog -->
    <AccountSplitDryRunDialog
      :show="data.showDryRunDialog"
      :split-id="data.splitId"
      :dry-run-result="data.dryRunResult"
      @close="handleDialogClose"
      @executed="handleExecuted"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.dashboard-search-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;

  .search-icon {
    color: rgba(200, 218, 255, 0.5);
  }

  &:focus-within {
    border-color: rgba(0, 176, 255, 0.4);
  }
}

.dashboard-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }
}

.search-block {
  position: relative;
}

.dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  max-height: 280px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.dropdown-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dropdown-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid rgba(120, 163, 255, 0.04);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.06);
  }
}

.dropdown-name {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
}

.dropdown-email {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.6);
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 24px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.9rem;

  .v-icon {
    color: #4fc3f7;
  }
}

.chip-label {
  font-weight: 500;
}

.chip-email {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.65);
}

.chip-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    background: rgba(244, 67, 54, 0.18);
    color: #ef5350;
  }
}

.notes-block {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);

  &.required::after {
    content: ' *';
    color: #ef5350;
  }
}

.form-textarea,
.form-input {
  padding: 10px 12px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  outline: none;
  font-family: inherit;
  width: 100%;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(120, 163, 255, 0.4);
    background: rgba(15, 23, 48, 0.8);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

.form-row {
  display: flex;
  gap: 12px;
  margin-top: 10px;

  &.two-col .form-group {
    flex: 1;
    min-width: 0;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.form-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 4px;
}

.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  margin-bottom: 18px;
  overflow-x: auto;
}

.tab-item {
  padding: 10px 18px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: rgba(200, 218, 255, 0.8);
    background: rgba(120, 163, 255, 0.04);
  }

  &.active {
    color: #00B0FF;
    border-bottom-color: #00B0FF;
  }
}

.asset-group {
  margin-top: 16px;

  &:first-child {
    margin-top: 0;
  }
}

.asset-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin: 0 0 8px;

  .v-icon {
    color: rgba(120, 163, 255, 0.7);
  }
}

.group-count {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
  background: rgba(120, 163, 255, 0.08);
  padding: 1px 8px;
  border-radius: 10px;
}

.data-table {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  gap: 10px;
  padding: 10px 12px;
  align-items: center;
}

.sub-cols {
  grid-template-columns: 40px 70px minmax(0, 1.4fr) minmax(0, 1.4fr) 90px 90px;
}

.domain-cols {
  grid-template-columns: 40px 70px minmax(0, 1.5fr) 100px;
}

.handle-cols {
  grid-template-columns: 40px 70px minmax(0, 1.5fr) 100px;
}

.site-cols {
  grid-template-columns: 40px 70px minmax(0, 1.3fr) 100px minmax(0, 1.2fr);
}

.mailbox-cols {
  grid-template-columns: 40px 70px minmax(0, 1.5fr) 90px 90px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.04);
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  &.selected {
    background: rgba(0, 176, 255, 0.06);
  }
}

.col-check input[type='checkbox'] {
  cursor: pointer;
  accent-color: #00B0FF;
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.85);
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;

  &.small {
    padding: 14px;
  }
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.info-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(0, 176, 255, 0.04);
  border: 1px solid rgba(0, 176, 255, 0.15);
  border-radius: 10px;
  padding: 12px;

  p {
    margin: 0;
    font-size: 0.85rem;
    color: rgba(220, 232, 255, 0.85);
  }

  .v-icon {
    color: #4fc3f7;
    margin-top: 1px;
  }
}

.action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #00B0FF, #1FE2D4);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(0, 176, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-text {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

@media (max-width: 700px) {
  .form-row.two-col {
    flex-direction: column;
  }
  .sub-cols,
  .domain-cols,
  .handle-cols,
  .site-cols,
  .mailbox-cols {
    grid-template-columns: 30px 1fr 1fr;
  }
}
</style>
